import { CreatePlayerRequestDto, PlayerCreationEntitiesResponseDto } from '@/lib/api/interfaces/players.interface';
import {
  CsvEntityFieldConfig,
  CsvImporterConfig,
  CsvValidationContext,
  CsvUpsertMode,
  CsvUpsertImportResult,
  CsvUpsertPreviewRowResult,
} from '@/lib/csv-import/csv-import.types';
import { useApiClient } from '@/lib/api/client';
import { usePlayersCreate, usePlayersCreationEntitiesQuery } from '@/lib/api/requests/players.requests';
import { useQueryClient } from '@tanstack/react-query';
import { playersKeys } from '@/lib/api/requests/players.requests';
import { PLAYER_POSITIONS } from '@/lib/utils/player-utils';

export interface PlayerImportMeta {
  creationEntities: PlayerCreationEntitiesResponseDto;
}

export function usePlayerCsvImportConfig(): CsvImporterConfig<CreatePlayerRequestDto, PlayerImportMeta> {
  const createMutation = usePlayersCreate();
  const { data: creationEntities } = usePlayersCreationEntitiesQuery();
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  const fields: CsvEntityFieldConfig<CreatePlayerRequestDto, PlayerImportMeta>[] = [
    {
      key: 'firstName',
      label: 'Vorname',
      type: 'string',
      required: true,
    },
    {
      key: 'lastName',
      label: 'Nachname',
      type: 'string',
      required: false,
      fallbackValue: ""
    },
    {
      key: 'nickname',
      label: 'Spitzname',
      type: 'string',
      required: false,
    },
    {
      key: 'position',
      label: 'Position',
      type: 'enum',
      required: false,
      enumValues: PLAYER_POSITIONS.map((p) => p.value),
      enumLabels: Object.fromEntries(PLAYER_POSITIONS.map((p) => [p.value, p.label])),
      enumNormalize: (value) => value.toLowerCase(),
      helpText: `Erlaubte Werte: ${PLAYER_POSITIONS.map((p) => p.label).join(', ')}`,
    },
    {
      key: 'jerseyNumber',
      label: 'Rückennummer',
      type: 'number',
      required: false,
    },
    {
      key: 'birthDate',
      label: 'Geburtsdatum',
      type: 'date',
      required: true,
      helpText: 'Bevorzugt TT.MM.JJJJ',
    },
    {
      key: 'birthDateConsent',
      label: 'Geburtsdatum-Einwilligung (ja/nein)',
      type: 'boolean',
      required: false,
      fallbackValue: false
    },
    {
      key: 'nationality',
      label: 'Nationalität',
      type: 'string',
      required: false,
    },
    {
      key: 'height',
      label: 'Größe (cm)',
      type: 'number',
      required: false,
    },
    {
      key: 'weight',
      label: 'Gewicht (kg)',
      type: 'number',
      required: false,
    },
    {
      key: 'biography',
      label: 'Biografie',
      type: 'string',
      required: false,
    },
    {
      key: 'belongsToTeamId',
      label: 'Mannschaft',
      type: 'relation',
      required: true,
      requiredRelation: true,
      relationKey: 'team',
      helpText:
        'Pflichtfeld. Wenn keine CSV-Spalte für die Mannschaft vorhanden ist, bitte in der Vorschau-Tabelle pro Zeile eine Mannschaft auswählen.',
    },
  ];

  const buildValidationContext = (): CsvValidationContext<PlayerImportMeta> => {
    return {
      meta: {
        creationEntities: creationEntities!,
      },
      relationOptions: {
        team: creationEntities
          ? creationEntities.teams.map((team) => ({ id: team._id, name: team.value }))
          : [],
      },
    };
  };

  const mode: CsvUpsertMode = 'upsert';

  return {
    entityName: 'Spieler',
    fields,
    mode,
    naturalKeyFields: ['firstName', 'lastName', 'birthDate', 'belongsToTeamId'],
    importEndpoint: async ({ mode, rows }): Promise<CsvUpsertImportResult> => {
      const res = await apiRequest('/players/import/csv', 'POST', { mode, rows });
      const result = res as CsvUpsertImportResult;
      // Nach erfolgreichem Import Players neu laden
      await queryClient.invalidateQueries({ queryKey: playersKeys.all });
      return result;
    },
    // Optional: Preview/Dry-Run-Endpunkt
    previewEndpoint: async ({ mode, rows }): Promise<CsvUpsertPreviewRowResult[]> => {
      const res = await apiRequest('/players/import/csv-preview', 'POST', { mode, rows });
      return (res as { rows: CsvUpsertPreviewRowResult[] }).rows;
    },
    // Fallback für Fälle ohne Batch-Import – bleibt aus Kompatibilitätsgründen bestehen
    onSubmitRow: (row) => createMutation.mutateAsync(row),
    buildValidationContext,
  };
}
